// The inference family as the console's Models surface reads it: discovery is
// the catalogue (02-DESIGN/05-inference/01-models-and-instances.md § discovery
// is the catalogue; decision 0011). Every ready instance registers as the
// micro service `inference` with the tenant's name for the model and what it
// serves as metadata; a caller scatters `$SRV.INFO.inference` on its own
// connection, inside the tenant's account, and what answered is what runs
// now — nobody keeps a catalogue beside the fleet, and nothing is cached. The
// console runs no inference, so nothing here speaks a capability: the
// transcript codec, the headers and the client that infers are the Go
// library's and the gateway's.
import {} from "@nats-io/nats-core";
import { validPublicKey } from "./envelope.js";
import { utf8Decode } from "./encoding.js";
import { judge } from "./identity.js";
import { newInbox } from "./inbox.js";
import { address, Family, serviceInfo, TOKEN_INFER, validToken } from "./subject.js";
import { workloadPath } from "./vault.js";
/** SERVICE is the micro service name every instance registers under, so one scatter finds them all; the tenant's name for the model is metadata. */
export const SERVICE = "inference";
/** Capability is one of the three things an instance may serve; each is its own endpoint under `workload.<id>.infer.<capability>`. */
export const Capability = {
    /** A generation: a transcript in, text out. */
    chat: "chat",
    /** Embeddings: texts in, vectors out. */
    embed: "embed",
    /** A decision: a state and typed questions in, probability-backed answers out. */
    decide: "decide",
};
/** CAPABILITIES lists the three. */
export const CAPABILITIES = Object.values(Capability);
/** validCapability reports whether s is one of the three. */
export function validCapability(s) {
    return CAPABILITIES.includes(s);
}
/** parseCapabilities reads a comma-separated list as the metadata carries it; what is not a capability is dropped. */
export function parseCapabilities(s) {
    const out = [];
    for (const p of s.split(",")) {
        const c = p.trim();
        if (validCapability(c)) {
            out.push(c);
        }
    }
    return out;
}
/**
 * The metadata an instance advertises on its service info; the sealing key
 * is the envelope's `sealing_key` as every sealed family advertises it.
 */
export const META_NAME = "inference.name";
export const META_DISPLAY_NAME = "inference.display-name";
export const META_PROVIDER = "inference.provider";
export const META_MODEL = "inference.model";
export const META_CAPABILITIES = "inference.capabilities";
export const META_FORMATS = "inference.formats";
/** MAX_NAME bounds the tenant's name for a model. */
export const MAX_NAME = 64;
/** validName reports whether s is a name a tenant may give a model: `[A-Za-z0-9._-]+`, at most MAX_NAME characters. */
export function validName(s) {
    return s !== "" && s.length <= MAX_NAME && /^[A-Za-z0-9._-]+$/.test(s);
}
/** SECRET_API_KEY is the name of the credential under the workload's own vault prefix: the provider's API key, stored by the admin and read by the instance alone. */
export const SECRET_API_KEY = "api-key";
/** credentialPath is `workload.<id>.api-key`, the vault path of an instance's credential (01-models-and-instances.md § an instance is a workload). */
export function credentialPath(workload) {
    return workloadPath(workload, SECRET_API_KEY);
}
/** workloadOf reads the workload id back from a capability subject, as a caller does from an instance's endpoints at discovery. */
export function workloadOf(subj) {
    let toks;
    try {
        toks = address(subj);
    }
    catch {
        return undefined;
    }
    const [family, id, infer, capability] = toks;
    if (toks.length !== 4 ||
        family !== Family.workload ||
        infer !== TOKEN_INFER ||
        capability === undefined ||
        !validCapability(capability) ||
        id === undefined ||
        !validToken(id)) {
        return undefined;
    }
    return id;
}
function str(v) {
    return typeof v === "string" ? v : "";
}
/**
 * parseInfo reads one discovery reply as an instance. An instance advertising
 * no sealing key, no name or no capability subject is left out, because
 * nothing could be sent to it.
 */
export function parseInfo(data) {
    let info;
    try {
        const parsed = JSON.parse(utf8Decode(data));
        if (typeof parsed !== "object" || parsed === null) {
            return undefined;
        }
        info = parsed;
    }
    catch {
        return undefined;
    }
    if (info.name !== SERVICE) {
        return undefined;
    }
    const md = typeof info.metadata === "object" && info.metadata !== null
        ? info.metadata
        : {};
    const name = str(md[META_NAME]);
    const inst = {
        id: str(info.id),
        workload: "",
        name,
        displayName: str(md[META_DISPLAY_NAME]) || name,
        provider: str(md[META_PROVIDER]),
        model: str(md[META_MODEL]),
        capabilities: parseCapabilities(str(md[META_CAPABILITIES])),
        formats: str(md[META_FORMATS])
            .split(",")
            .map((p) => p.trim())
            .filter((p) => p !== ""),
        sealingKey: str(md["sealing_key"]),
    };
    if (Array.isArray(info.endpoints)) {
        const endpoints = info.endpoints;
        for (const ep of endpoints) {
            if (typeof ep === "object" && ep !== null && "subject" in ep) {
                const w = workloadOf(str(ep.subject));
                if (w !== undefined) {
                    inst.workload = w;
                    break;
                }
            }
        }
    }
    if (inst.workload === "" || !validPublicKey(inst.sealingKey) || inst.name === "") {
        return undefined;
    }
    return inst;
}
/** metadataOf reads a discovery reply's metadata; empty when it has none. */
function metadataOf(data) {
    try {
        const parsed = JSON.parse(utf8Decode(data));
        if (typeof parsed === "object" && parsed !== null && "metadata" in parsed) {
            const md = parsed.metadata;
            if (typeof md === "object" && md !== null) {
                return md;
            }
        }
    }
    catch {
        // not an answer
    }
    return {};
}
/** The server's no-responders status, delivered to the reply inbox when nothing is subscribed. */
const NO_RESPONDERS = 503;
/** nextWithin waits for a message for at most ms; undefined when none came. */
async function nextWithin(it, ms, signal) {
    if (signal?.aborted) {
        throw signal.reason instanceof Error
            ? signal.reason
            : new DOMException("inference: aborted", "AbortError");
    }
    let timer;
    let onAbort;
    try {
        const result = await Promise.race([
            it.next(),
            new Promise((resolve) => {
                timer = setTimeout(() => {
                    resolve(undefined);
                }, ms);
            }),
            new Promise((_, reject) => {
                if (signal) {
                    onAbort = () => {
                        const reason = signal.reason;
                        reject(reason instanceof Error ? reason : new DOMException("inference: aborted", "AbortError"));
                    };
                    signal.addEventListener("abort", onAbort, { once: true });
                }
            }),
        ]);
        if (result === undefined || result.done === true) {
            return undefined;
        }
        return result.value;
    }
    finally {
        clearTimeout(timer);
        if (signal && onAbort) {
            signal.removeEventListener("abort", onAbort);
        }
    }
}
/** kept is what discovery keeps: for each name the attested instances, or — no attested one carrying it — the unattested, each with a warning naming it. */
function kept(all) {
    const attested = new Set(all.filter((j) => j.verdict === "attested").map((j) => j.inst.name));
    const out = [];
    for (const j of all) {
        if (j.verdict === "unattested") {
            if (attested.has(j.inst.name)) {
                continue;
            }
            console.warn(`inference: kept an unattested instance; its image predates decision 0026: name=${j.inst.name} workload=${j.inst.workload}`);
        }
        out.push(j.inst);
    }
    return out;
}
/**
 * discover scatters `$SRV.INFO.inference` on the caller's own connection and
 * returns every instance that answered and that the platform vouches for —
 * its attestation verifies against keys and names tenant, the instance's
 * name, workload and key (decision 0026); an unattested answer is kept, when
 * lenient, for a name no attested instance carries; any other answer is
 * dropped uncounted, and two answers from one workload are one, the attested
 * of the two where one is — what the tenant has
 * is what runs now — until the window closes with no answer, the quiet period passes after
 * the last one, or the server says nobody is subscribed. Nothing is cached: a
 * cached scatter cannot know it was complete.
 */
export async function discover(nc, opts = {}) {
    const inbox = newInbox(nc);
    const sub = nc.subscribe(inbox);
    const it = sub[Symbol.asyncIterator]();
    const all = [];
    const seen = new Map();
    try {
        nc.publish(serviceInfo(SERVICE), undefined, { reply: inbox });
        let wait = opts.window ?? 1000;
        for (;;) {
            const msg = await nextWithin(it, wait, opts.signal);
            if (msg === undefined || (msg.headers?.code ?? 0) === NO_RESPONDERS) {
                return kept(all);
            }
            const inst = parseInfo(msg.data);
            if (inst !== undefined) {
                const verdict = await judge(metadataOf(msg.data), opts.keys, opts.tenant ?? "", inst.name, inst.workload);
                if (verdict === "refused" ||
                    (verdict === "unattested" && (opts.lenient !== true || opts.keys === undefined))) {
                    // dropped uncounted
                }
                else {
                    inst.attested = verdict === "attested";
                    const at = seen.get(inst.workload);
                    if (at === undefined) {
                        seen.set(inst.workload, all.length);
                        all.push({ inst, verdict });
                    }
                    else if (verdict === "attested" && all[at]?.verdict === "unattested") {
                        all[at] = { inst, verdict };
                    }
                }
            }
            wait = opts.quiet ?? 100;
        }
    }
    finally {
        sub.unsubscribe();
    }
}
//# sourceMappingURL=inference.js.map